import { useState, useEffect, useCallback } from 'react';
import { gpsApi } from '../../api/gps.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Spinner, Select } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function GpsDailyReportPage() {
  const { addToast } = useUI();
  const [date, setDate] = useState(todayStr());
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [report, setReport] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  const loadEmployees = useCallback(async (d) => {
    setLoadingEmployees(true);
    try {
      const res = await gpsApi.getDailyReportEmployees(d);
      const list = res.data?.employees || [];
      setEmployees(list);
      setEmployeeId(list.length ? String(list[0].employee_id) : '');
    } catch {
      addToast('خطا در دریافت لیست کارمندان', 'error');
    } finally {
      setLoadingEmployees(false);
    }
  }, [addToast]);

  useEffect(() => { loadEmployees(date); }, [date, loadEmployees]);

  useEffect(() => {
    if (!employeeId) { setReport(null); return; }
    let cancelled = false;
    setLoadingReport(true);
    gpsApi.getDailyReport(employeeId, date)
      .then((res) => { if (!cancelled) setReport(res.data); })
      .catch(() => { if (!cancelled) addToast('خطا در دریافت گزارش', 'error'); })
      .finally(() => { if (!cancelled) setLoadingReport(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, date, addToast]);

  useAutoRefresh(() => {
    if (employeeId) {
      gpsApi.getDailyReport(employeeId, date).then((res) => setReport(res.data)).catch(() => {});
    }
  });

  const employeeOptions = employees.map((e) => ({ value: String(e.employee_id), label: e.employee_name }));

  return (
    <div className="fade-in">
      <PageHeader
        title="گزارش موقعیت مکانی کارمندان (GPS)"
        subtitle="مسیر حرکت هر کارمند در طول روز و مطابقت آن با مکان‌های مجاز از پیش تعریف‌شده"
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, maxWidth: 640 }}>
        <div style={{ flex: '1 1 180px' }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>تاریخ</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
          />
        </div>
        <Select
          label="کارمند"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          options={employeeOptions.length ? employeeOptions : [{ value: '', label: 'داده‌ای برای این تاریخ نیست' }]}
          wrapStyle={{ flex: '1 1 220px' }}
        />
      </div>

      {loadingEmployees || loadingReport ? <Spinner /> : (
        <>
          {(!report || report.pings.length === 0) && (
            <div style={{ padding: 20, fontSize: 13, color: '#94a3b8' }}>موقعیتی برای این کارمند/تاریخ ثبت نشده است.</div>
          )}

          {report && report.pings.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', maxWidth: 720 }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8faff', textAlign: 'right' }}>
                      <th style={{ padding: '10px 14px' }}>ساعت</th>
                      <th style={{ padding: '10px 14px' }}>مختصات</th>
                      <th style={{ padding: '10px 14px' }}>نزدیک‌ترین مکان مجاز</th>
                      <th style={{ padding: '10px 14px' }}>وضعیت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.pings.map((p) => (
                      <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px' }}>{p.recorded_at}</td>
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace' }}>
                          {p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {p.nearest_place
                            ? `${p.nearest_place.place_name} (${p.nearest_place.distance_meters} متر)`
                            : '—'}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {p.nearest_place?.within_radius ? (
                            <span style={{ color: '#16a34a', fontWeight: 700 }}>در محل حضور دارد</span>
                          ) : (
                            <span style={{ color: '#dc2626', fontWeight: 700 }}>خارج از مکان‌های مجاز</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
